import type { ReactNode } from "react";
import type { WorldGraphNode } from "@/lib/world-graph-types";
import "./world-model-node-icon.css";

export type WorldModelNodeIconProps = {
  node?: WorldGraphNode;
  source?: { id: string; label: string; kind?: string; url?: string };
  className?: string;
};

type IconKind = "institution" | "bank" | "person" | "company" | "news" | "video" | "audio" | "social" | "science" | "market" | "metric" | "portfolio" | "system" | "event" | "claim" | "evidence" | "hypothesis" | "forecast" | "outcome" | "simulation" | "source";

const shapes: Record<IconKind, ReactNode> = {
  institution: <><path d="m3 8 9-5 9 5M4 9h16M5 19V10m5 9v-9m4 9v-9m5 9v-9M3 21h18" /></>,
  bank: <><path d="m3 7 9-4 9 4M3 9h18M5 10v8m14-8v8M3 21h18M3 18h18m10-7h-2a1.5 1.5 0 0 0 0 3h2a1.5 1.5 0 0 1 0 3h-2m1-7v8" /></>,
  person: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></>,
  company: <><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M9 7h1m4 0h1M9 11h1m4 0h1M9 15h1m4 0h1m-4 6v-3h2v3" /></>,
  news: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h3m-3 3h8" /></>,
  video: <><rect x="3" y="5" width="18" height="14" rx="4" /><path d="m10 9 5 3-5 3Z" /></>,
  audio: <><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 11v1a7 7 0 0 0 14 0v-1m-7 8v3m-3 0h6" /></>,
  social: <><circle cx="12" cy="12" r="4" /><path d="M16 8v6a2 2 0 0 0 4 0v-2a8 8 0 1 0-3 6.25" /></>,
  science: <><path d="M9 3h6m-5 0v7l-5 8a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-8V3M8 15h8" /><circle cx="10" cy="18" r=".5" /></>,
  market: <><path d="M4 3v18h17M7 15l4-5 4 3 6-8m-5 0h5v5" /></>,
  metric: <><path d="M3 12h4l3-7 4 14 3-7h4" /></>,
  portfolio: <><rect x="3" y="7" width="18" height="14" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12a24 24 0 0 0 18 0m-9 0v3" /></>,
  system: <><rect x="9" y="2" width="6" height="6" rx="1.5" /><rect x="2" y="16" width="6" height="6" rx="1.5" /><rect x="16" y="16" width="6" height="6" rx="1.5" /><path d="M12 8v4H5v4m7-4h7v4" /></>,
  event: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4m10-4v4M3 10h18m-13 5h3m-3 3h6" /></>,
  claim: <><path d="M20 15a3 3 0 0 1-3 3H9l-5 3v-6a8 8 0 0 1 0-10h13a3 3 0 0 1 3 3Z" /><path d="M8 9h8m-8 4h5" /></>,
  evidence: <><path d="M13 3H5v18h14V9l-6-6Zm0 0v6h6M8 15l2 2 5-5" /></>,
  hypothesis: <><path d="M9 18h6m-6 3h6M8 15c0-2-3-3-3-7a7 7 0 0 1 14 0c0 4-3 5-3 7M10 6a3 3 0 0 1 3 0" /></>,
  forecast: <><path d="M4 20V4m0 16h16M7 16l4-5 3 2 6-8m-5 0h5v5" strokeDasharray="2 3" /><circle cx="7" cy="16" r="1.5" /></>,
  outcome: <><circle cx="12" cy="12" r="9" /><path d="m7 12 3 3 7-7" /></>,
  simulation: <><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8" cy="8" r="1" /><circle cx="16" cy="8" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="8" cy="16" r="1" /><circle cx="16" cy="16" r="1" /></>,
  source: <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></>,
};

function value(node: WorldGraphNode | undefined, key: string) {
  const field = node?.attributes[key];
  return typeof field === "string" ? field : "";
}

function sourceIconKind(source: WorldModelNodeIconProps["source"], node: WorldGraphNode | undefined): IconKind {
  const kind = [source?.kind, value(node, "sourceKind"), value(node, "actorKind"), value(node, "surfaceKind")].filter(Boolean).join(" ").toLowerCase();
  const name = (source?.label ?? node?.label ?? "").toLowerCase();
  const url = source?.url ?? node?.url ?? "";
  let hostname = "";
  try { hostname = new URL(url).hostname.toLowerCase().replace(/^www\./, ""); } catch { /* A source URL is optional. */ }
  if (/person|individual|human/.test(kind)) return "person";
  if (/bank|central bank/.test(kind) || /federal reserve|bank of|central bank/.test(name)) return "bank";
  if (/youtube|video/.test(kind) || hostname === "youtube.com" || hostname === "youtu.be" || /youtube/.test(name)) return "video";
  if (/podcast|audio/.test(kind) || /podcast/.test(name)) return "audio";
  if (/account|social/.test(kind) || /^(x\.com|twitter\.com|reddit\.com|instagram\.com)$/.test(hostname) || name.startsWith("@")) return "social";
  if (/market|exchange|broker/.test(kind) || value(node, "category") === "market") return "market";
  if (/university|science|research institute|laboratory/.test(kind)) return "science";
  if (/company|issuer|business|firm|ai lab/.test(kind)) return "company";
  if (/institution|government|agency|regulator|fund|investor/.test(kind)) return "institution";
  if (/news|media|publication|article|feed|documentation|filing|blog|substack/.test(kind) || value(node, "category") === "media") return "news";
  return node?.kind === "surface" ? "news" : "source";
}

function iconKind({ source, node }: WorldModelNodeIconProps): IconKind {
  if (source || !node || node.kind === "source" || node.kind === "surface") return sourceIconKind(source, node);
  switch (node.kind) {
    case "system": return "system";
    case "variable": return "metric";
    case "position": return "portfolio";
    case "projection":
    case "forecast": return "forecast";
    default: return node.kind;
  }
}

/** Decorative identity mark: the adjacent node name remains the accessible label. */
export default function WorldModelNodeIcon({ node, source, className }: WorldModelNodeIconProps) {
  // Only exact canonical identities receive brand marks; a claim mentioning a brand does not.
  const canonicalId = source?.id ?? (node?.kind === "source" && node.id.startsWith("source:") ? node.id.slice(7) : "");
  const brand = canonicalId === "openai" ? "openai"
    : canonicalId === "anthropic" ? "anthropic"
      : canonicalId === "coinbase" || canonicalId === "coinbase-global" ? "coinbase" : null;
  const kind = iconKind({ source, node });
  return <span className={["world-model-node-icon", className].filter(Boolean).join(" ")} data-icon={brand ?? kind} aria-hidden="true">
    {brand === "openai" ? <span className="world-model-node-icon__openai" />
      : brand ? <b className="world-model-node-icon__brand">{brand === "anthropic" ? "AI" : "C"}</b>
        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" focusable="false">{shapes[kind]}</svg>}
  </span>;
}
